import { Fragment, useState, useMemo } from 'react';
import DataTableToolbar from './DataTableToolbar';
import { TablePagination, Button, MapView } from '@shared/components';
import styles from './DataTable.module.css';
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react';

function DataTable({
  data,
  columns,
  columnFilters = [],
  onDrawerButtonAction = null,
  actionLabel = '',
  includeMap = false,
  onRowClick = null,
}) {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowName, setSelectedRowName] = useState(null);

  const filterOptions = useMemo(() => {
    const opts = {};
    columnFilters.forEach((field) => {
      const values = new Set();
      data.forEach((row) => {
        if (row[field] !== undefined && row[field] !== null) {
          values.add(row[field]);
        }
      });
      opts[field] = [...values].sort();
    });
    return opts;
  }, [data, columnFilters]);

  const filteredData = useMemo(() => {
    return data.filter((row) => {
      for (let [field, value] of Object.entries(filters)) {
        if (value && row[field] !== value) {
          return false;
        }
      }

      if (search) {
        const s = search.toLowerCase();
        const matches = columns.some((col) => {
          return String(row[col.key] ?? "").toLowerCase().includes(s);
        });
        if (!matches) return false;
      }

      return true;
    });
  }, [data, filters, search, columns]);

  const sorted = useMemo(() => {
    if (!sortKey) return filteredData;
    return [...filteredData].sort((a, b) => {
      const x = a[sortKey];
      const y = b[sortKey];
      if (x < y) return sortDir === 'asc' ? -1 : 1;
      if (x > y) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortKey, sortDir]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  function handleSort(col) {
    if (sortKey === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(col);
      setSortDir('asc');
    }
  }

  const toggleRow = (id, name) => {
    setSelectedRow((prev) => (prev === id ? null : id));
    setSelectedRowName((prev) => (prev === name ? null : name));
  };

  const coordinates = useMemo(() => {
    if (!selectedRowName) return [];
    return extractCoords(data[selectedRow], columns);
  }, [selectedRowName]);

  function extractCoords(items, columns) {
    const nameColumn = columns.find(col =>
      col.label.toLowerCase().includes("name")
    );

    const nameKey = nameColumn ? nameColumn.key : null;

    const latitudeKey = Object.keys(items).find(col =>
      col.toLowerCase().includes("lat")
    );

    const longitudeKey = Object.keys(items).find(col =>
      col.toLowerCase().includes("long")
    );

    if (!latitudeKey || !longitudeKey) return [];

    const label = nameKey ? items[nameKey] : undefined;

    const coords = [{
      lat: Number(items[latitudeKey]),
      lng: Number(items[longitudeKey]),
      label: label ? String(items[nameKey]) : undefined,
    }].filter(Boolean);
    return coords;
  }


  return (
    <div className={styles.mainContainer}>
      <DataTableToolbar
        columns={columns}
        filterableFields={columnFilters}
        filterOptions={filterOptions}
        filters={filters}
        setFilters={setFilters}
        search={search}
        setSearch={setSearch}
      />

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr className={styles.tableHeadRow}>
              {columns.map((col) => (
                <th key={col.key} className={styles.tableCell}>
                  <button
                    className={[
                      styles.sortButton,
                      sortKey === col.key && styles.active
                    ].filter(Boolean).join(" ")}
                    onClick={() => handleSort(col.key)}
                  >
                    {col.label}
                    <span className={styles.sortIcon}>
                      {sortKey === col.key ? (sortDir === 'asc' ? <ChevronUp /> : <ChevronDown />) : <ChevronsUpDown />}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className={styles.tableBody}>
            {/* Render row entries */}
            {paginated.map((row, index) => {
              const id = row.id ?? index;
              const isOpen = selectedRow === id;
              const rowName = row[columns[0].key];
              return (
                <Fragment key={id}>
                  <tr
                    className={[
                      styles.tableRow,
                      selectedRow === id && styles.selectedRow

                    ].filter(Boolean).join(" ")}
                    onClick={onRowClick ? () => onRowClick(id, row) : () => toggleRow(id, rowName)}
                  >
                    {columns.map((col) => (
                      <td key={col.key} className={styles.tableBodyCell}>
                        {row[col.key]}
                      </td>
                    ))}
                  </tr>

                  {/* Drawer detail */}
                  <tr className={styles.detailsRow}>
                    <td colSpan={columns.length} className={styles.detailsCell}>
                      <div className={[
                        styles.collapse,
                        isOpen && styles.open
                      ].filter(Boolean).join(" ")}>
                        <div className={styles.drawerWrapper}>
                          <div className={styles.drawerInfo}>
                            <h3>Details</h3>
                            {Object.entries(row).map(([k, v]) => (
                              <Fragment key={k}>
                                <div className={styles.drawerEntry}>
                                  <span>{k}</span>
                                  <span>{String(v)}</span>
                                </div>
                              </Fragment>
                            ))}

                            {onDrawerButtonAction && (
                              <Button
                                onClick={() => onDrawerButtonAction(row)}
                              >
                                {actionLabel}
                              </Button>
                            )}
                          </div>
                          {includeMap && (
                            <div className={styles.drawerMap}>
                              <MapView markers={coordinates} />
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <TablePagination
        count={sorted.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100]}
      />
    </div>
  );
}

export default DataTable;